import type {
  ConsumptionLineDto,
  FefoSuggestionResponse,
  LotResponse,
} from '@/api/generated/model';

import { isFefoOverride, suggestFefoLot } from './fefo';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  THE CONSUMPTION LINE — what was suggested, and what was actually drawn
 * ════════════════════════════════════════════════════════════════════════════
 *
 * The editor's state, and the rules that turn it into `ConsumptionLineDto`s.
 * Kept out of the component so the rules can be tested without a DOM, because
 * these are the rules that decide whether cost-per-patient is real:
 *
 *  · `suggestedLotId` is what FEFO proposed. It is display + comparison only.
 *    It is NEVER what gets posted.
 *  · `lotId` is what the doctor chose. It is what gets posted, always.
 *  · When the two differ, `overrideReason` is mandatory — the backend rejects
 *    the write without it, and the form must not let a clinician discover that
 *    after finishing a twelve-line procedure.
 *
 * `SessionConsumption` keeps both (`fefoSuggestedLotId` and `lotId`), which is
 * what lets the clinic later ask "how often do we skip the expiring vial, and
 * why" — a question that only has an answer because the panel never quietly
 * substitutes one for the other.
 */

export interface ConsumptionLine {
  /** Local row id. Not the server's — the row exists before it is written. */
  key: string;
  materialId: string;
  materialName: string;
  /** Unit word for the field suffix («واحد», «سی‌سی»). */
  unitLabel: string;
  quantityMilli: number | null;
  /** The FEFO answer. Never posted; shown next to the choice. */
  suggestedLotId: string | null;
  /** The lot the doctor actually opened. Posted verbatim. */
  lotId: string | null;
  isWastage: boolean;
  overrideReason: string;
}

let counter = 0;
/** A stable local key. `crypto.randomUUID` is absent in jsdom and on old Safari. */
export function nextLineKey(): string {
  counter += 1;
  return `line-${counter}`;
}

/** Seeds a line from the server's per-procedure FEFO suggestion. */
export function lineFromSuggestion(
  suggestion: FefoSuggestionResponse,
  unitLabel: string,
): ConsumptionLine {
  return {
    key: nextLineKey(),
    materialId: suggestion.materialId,
    materialName: suggestion.materialName,
    unitLabel,
    quantityMilli: suggestion.quantityMilli,
    suggestedLotId: suggestion.lotId ?? null,
    // Pre-selected to the suggestion — that is what makes FEFO the default
    // rather than a hint the operator has to act on. Changing it is one click.
    lotId: suggestion.lotId ?? null,
    isWastage: false,
    overrideReason: '',
  };
}

/**
 * Re-derives the suggestion after the quantity or the material changed.
 *
 * The chosen lot is carried through untouched unless it is no longer usable for
 * the new quantity, in which case it falls back to the fresh suggestion — a
 * silently impossible selection is worse than a visibly changed one, because
 * the server would reject the whole session at submit time with the operator
 * having no idea which line was at fault.
 */
export function reconcileLine(
  line: ConsumptionLine,
  lots: readonly LotResponse[],
): ConsumptionLine {
  const quantity = line.quantityMilli ?? 0;
  const suggestion = suggestFefoLot(lots, quantity);
  const suggestedLotId = suggestion.lot?.id ?? null;

  const chosen = lots.find((lot) => lot.id === line.lotId) ?? null;
  const chosenStillFits =
    chosen !== null && chosen.isConsumable && chosen.quantityRemainingMilli >= quantity;

  const lotId = chosenStillFits ? line.lotId : suggestedLotId;

  return {
    ...line,
    suggestedLotId,
    lotId,
    // Dropping back onto the suggestion is not an override, so the reason it
    // carried is no longer about anything.
    overrideReason: isFefoOverride(suggestedLotId, lotId) ? line.overrideReason : '',
  };
}

/**
 * ── THE SERVER'S BASELINE ───────────────────────────────────────────────────
 *
 * `ConsumptionPlanResponse.defaultFirstLotId` is the lot the allocator would
 * draw from first had the caller named none — the baseline the write-time
 * `isOverride` is measured against. The panel's local FEFO copy answers a
 * different question ("which single lot covers the quantity"), and on a split
 * draw the two disagree: the panel either let a line through without a reason
 * and the server refused the whole session, or it demanded a justification the
 * clinician did not owe.
 *
 * So the baseline is asked of the server (`useOverrideBaselines`) and, once it
 * has answered, wins. Keyed by material and quantity because the answer
 * depends on both. Three states:
 *
 *  · a lot id  — choosing any other lot is an override;
 *  · `null`    — the server says EVERY choice is an override;
 *  · absent    — no answer yet (or a server without the field): local rule.
 */
export type OverrideBaselines = Readonly<Record<string, string | null>>;

export function baselineKey(materialId: string, quantityMilli: number | null): string {
  return `${materialId}:${quantityMilli ?? 0}`;
}

/** The lot a choice is measured against: the server's baseline when known, else the local suggestion. */
export function baselineLotIdOf(
  line: Pick<ConsumptionLine, 'materialId' | 'quantityMilli' | 'suggestedLotId'>,
  baselines: OverrideBaselines | undefined,
): { lotId: string | null; fromServer: boolean } {
  const key = baselineKey(line.materialId, line.quantityMilli);
  if (baselines && Object.prototype.hasOwnProperty.call(baselines, key)) {
    return { lotId: baselines[key] ?? null, fromServer: true };
  }
  return { lotId: line.suggestedLotId, fromServer: false };
}

/** Does this line owe a written reason? */
export function overrideRequiresReason(
  line: ConsumptionLine,
  baselines: OverrideBaselines | undefined,
): boolean {
  if (!line.lotId) return false;
  const baseline = baselineLotIdOf(line, baselines);
  if (!baseline.fromServer) return isFefoOverride(baseline.lotId, line.lotId);
  // A null baseline from the server is not "nothing to override" — it is
  // "any choice is an override", the opposite of the local rule's null.
  return baseline.lotId === null || baseline.lotId !== line.lotId;
}

export interface LineProblem {
  key: string;
  message: string;
}

/** `simple`: the clinic keeps simple stock (no lot tracking in its plan). */
export interface StockOptions {
  simple?: boolean;
}

/**
 * Everything wrong with the current lines, in the order a clinician would fix
 * them. An empty array means the form may be submitted.
 */
export function validateLines(
  lines: readonly ConsumptionLine[],
  baselines?: OverrideBaselines,
  options: StockOptions = {},
): LineProblem[] {
  const problems: LineProblem[] = [];

  for (const line of lines) {
    if (line.quantityMilli === null || line.quantityMilli <= 0) {
      problems.push({ key: line.key, message: `مقدار مصرف «${line.materialName}» رو وارد کنید.` });
      continue;
    }
    if (!Number.isInteger(line.quantityMilli)) {
      // Unreachable through `UnitsInput`, which rounds at the boundary. Kept as
      // an assertion: a non-integer milli quantity reaching the API would be
      // rejected as a validation error with no field name attached to it.
      problems.push({ key: line.key, message: `مقدار «${line.materialName}» عدد صحیح نیست.` });
      continue;
    }
    // Simple stock names no batch: the server draws it, so there is nothing
    // to choose and nothing to justify.
    if (options.simple) continue;
    if (!line.lotId) {
      problems.push({
        key: line.key,
        message: `سری ساخت مصرفی «${line.materialName}» رو انتخاب کنید.`,
      });
      continue;
    }
    if (overrideRequiresReason(line, baselines) && line.overrideReason.trim() === '') {
      problems.push({
        key: line.key,
        message: `برای «${line.materialName}» به‌جای سری ساخت پیشنهادی سری دیگری انتخاب شده؛ دلیل اون رو بنویسید.`,
      });
    }
  }

  return problems;
}

/**
 * The wire form.
 *
 * `lotId` is the CHOSEN lot, never the suggestion. `overrideReason` is sent
 * only when the choice actually differs — sending it otherwise would record a
 * justification for a decision nobody made, and the override statistics this
 * data exists to produce would count every line.
 */
export function toConsumptionDtos(
  lines: readonly ConsumptionLine[],
  baselines?: OverrideBaselines,
  options: StockOptions = {},
): ConsumptionLineDto[] {
  if (options.simple) {
    // SIMPLE stock (no `lotInventory`): material and quantity only — never a
    // `lotId`, never an override. The server draws from what is on the shelf
    // (the pool, or a downgraded clinic's old batches first-expiring-first),
    // and its planner's FEFO answer is the record, so the ONE definition of an
    // override (`FefoAllocation.fefoSuggestedLotId`) is never contradicted.
    return lines
      .filter((line) => line.quantityMilli !== null && line.quantityMilli > 0)
      .map((line) => ({
        materialId: line.materialId,
        quantityMilli: line.quantityMilli as number,
        ...(line.isWastage ? { isWastage: true } : {}),
      }));
  }
  return lines
    .filter((line) => line.quantityMilli !== null && line.quantityMilli > 0 && line.lotId !== null)
    .map((line) => {
      const override = overrideRequiresReason(line, baselines);
      return {
        materialId: line.materialId,
        lotId: line.lotId as string,
        quantityMilli: line.quantityMilli as number,
        ...(line.isWastage ? { isWastage: true } : {}),
        ...(override ? { overrideReason: line.overrideReason.trim() } : {}),
      };
    });
}
